import React, { useEffect, useState } from "react";

/* eslint-disable no-unused-vars */
import {
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  message,
  Row,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd";
import fileDownload from "js-file-download";
import moment from "moment";

import { InfoCircleOutlined } from "@ant-design/icons";

import useDebounce from "../../hooks/useDebounce";
import { getRefunds } from "../api";

const currencyMappings = {
  INR: "₹",
  USD: "$",
  EUR: "€",
};

const CREATOR_NAME_LIMIT = 25;
const SUBSCRIBER_NAME_LIMIT = 20;

const truncate = (str, limit) =>
  str && str.length > limit ? `${str.slice(0, limit)}…` : str;

const Refunds = () => {
  const [refunds, setRefunds] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [refundsLoading, setRefundsLoading] = useState(false);
  const [totalRefundValue, setTotalRefundValue] = useState(0);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(
    moment().add(-7, "days").startOf("day")
  );
  const [endDate, setEndDate] = useState(moment().endOf("day"));
  const debouncedSearch = useDebounce({
    value: search,
    callback: () => setPage(1),
  });
  const [tableKey, setTableKey] = useState(Date.now());

  const getRefundsData = async () => {
    setRefundsLoading(true);
    try {
      const resp = await getRefunds(
        startDate.toISOString(),
        endDate.toISOString(),
        debouncedSearch,
        page,
        100
      );
      if (resp?.code === 0) {
        setRefunds(resp?.result?.orders);
        setTotal(resp?.result?.orderCount);
        setTotalRefundValue(resp?.result?.totalRefund);
        setTableKey(Date.now());
      } else {
        message.error(resp?.message || "Something went wrong");
      }
    } catch (err) {
      message.error(err?.message || "Something went wrong");
    } finally {
      setRefundsLoading(false);
    }
  };

  const getRefundsCSV = async () => {
    try {
      const resp = await getRefunds(
        startDate.toISOString(),
        endDate.toISOString(),
        debouncedSearch,
        undefined,
        undefined,
        true
      );
      fileDownload(resp, `refunds_${startDate}_${endDate}.csv`);
    } catch (err) {
      message.error(err?.message || "Something went wrong");
    }
  };

  const refundsTable = () => {
    const nameStyle = { fontWeight: 500, fontSize: 14 };
    const columns = [
      {
        title: "Order ID",
        dataIndex: "_id",
        key: "_id",
      },
      {
        title: "Creator",
        dataIndex: "creatorName",
        key: "creatorName",
        render: (text, record) => (
          <Space size="small" direction="vertical">
            <Avatar src={record?.creatorProfilePicUrl} size={32} />
            <Tooltip
              title={
                record?.creatorName?.length > CREATOR_NAME_LIMIT
                  ? record?.creatorName
                  : ""
              }
            >
              <Typography style={nameStyle}>
                {truncate(record?.creatorName, CREATOR_NAME_LIMIT)}
              </Typography>
            </Tooltip>
            <Typography>{record?.mangoName}</Typography>
          </Space>
        ),
      },
      {
        title: "Subscriber",
        dataIndex: "fanName",
        key: "fanName",
        render: (text, record) => (
          <Space size="small" direction="vertical" align="start">
            <Avatar src={record?.fanImg} size={32} />
            <Space size={6} align="center">
              <Tooltip
                title={
                  record?.fanName?.length > SUBSCRIBER_NAME_LIMIT
                    ? record?.fanName
                    : ""
                }
              >
                <Typography style={nameStyle}>
                  {truncate(record?.fanName, SUBSCRIBER_NAME_LIMIT)}
                </Typography>
              </Tooltip>
              <Tooltip
                overlayStyle={{ maxWidth: "none" }}
                overlayInnerStyle={{ maxWidth: "none", width: "max-content" }}
                title={
                  <div style={{ lineHeight: 1.6, whiteSpace: "nowrap" }}>
                    <div>Name: {record?.fanName || "N/A"}</div>
                    <div>Email: {record?.fanEmail || "N/A"}</div>
                    <div>Phone: {record?.fanPhone || "N/A"}</div>
                    <div>Country: {record?.fanCountry || "N/A"}</div>
                  </div>
                }
              >
                <InfoCircleOutlined style={{ color: "#9ca3af" }} />
              </Tooltip>
            </Space>
          </Space>
        ),
      },
      {
        title: "Order Details",
        dataIndex: "amount",
        key: "amount",
        render: (text, record) => {
          const currencySymbol =
            currencyMappings[record?.subscriberCurrency || "INR"];
          const commissionCharged =
            record?.[(record?.subscriberCurrency || "INR")?.toLowerCase()]
              ?.commissionCharged;
          return (
            <Space size={6} align="center">
              <Typography>
                {currencySymbol}
                {record?.amount}
              </Typography>
              <Tooltip
                overlayStyle={{ maxWidth: "none" }}
                overlayInnerStyle={{
                  maxWidth: "none",
                  width: "max-content",
                }}
                title={
                  <div style={{ lineHeight: 1.6, whiteSpace: "nowrap" }}>
                    <div>
                      Amount (with GST): {currencySymbol}
                      {record?.amount}
                    </div>
                    {record?.gst !== undefined ? (
                      <div>
                        GST: {currencySymbol}
                        {record?.gst}
                      </div>
                    ) : null}
                    {record?.convenienceFee !== undefined ? (
                      <div>
                        Convenience Fee: {currencySymbol}
                        {record?.convenienceFee}
                      </div>
                    ) : null}
                    <div>
                      Commission charged: {currencySymbol}
                      {commissionCharged}
                    </div>
                  </div>
                }
              >
                <InfoCircleOutlined style={{ color: "#9ca3af" }} />
              </Tooltip>
            </Space>
          );
        },
      },
      {
        title: "Refund Details",
        dataIndex: "refundMetaDate",
        key: "refundMetaDate",
        render: (text, record) => {
          const refundCurrencySymbol =
            currencyMappings[record?.refundMetaCurrency || "INR"];
          return (
            <Space size={6} align="center">
              <Typography>
                {refundCurrencySymbol}
                {record?.refundAmount}
              </Typography>
              <Tooltip
                overlayStyle={{ maxWidth: "none" }}
                overlayInnerStyle={{
                  maxWidth: "none",
                  width: "max-content",
                }}
                title={
                  <div style={{ lineHeight: 1.6, whiteSpace: "nowrap" }}>
                    <div>
                      Refunded Amount: {refundCurrencySymbol}
                      {record?.refundAmount}
                    </div>
                    {record?.totalAmountRefunded !== undefined ? (
                      <div>
                        Total Refunded Amount: {refundCurrencySymbol}
                        {record?.totalAmountRefunded}
                      </div>
                    ) : null}
                    {record?.refundMetaDate ? (
                      <div>
                        Refund Date:{" "}
                        {moment(record?.refundMetaDate).format(
                          "MMMM Do YYYY, h:mm:ss a"
                        )}
                      </div>
                    ) : null}
                    {record?.refundMetaStatus ? (
                      <div>Status: {record?.refundMetaStatus}</div>
                    ) : null}
                    {record?.refundMetaRefundId ? (
                      <div>Refund Id: {record?.refundMetaRefundId}</div>
                    ) : null}
                    {record?.paymentProvider ? (
                      <div>Payment Gateway: {record?.paymentProvider}</div>
                    ) : null}
                    {record?.refundIndex !== undefined ? (
                      <div>Refund Number: {record?.refundIndex + 1}</div>
                    ) : null}
                  </div>
                }
              >
                <InfoCircleOutlined style={{ color: "#9ca3af" }} />
              </Tooltip>
            </Space>
          );
        },
      },
      {
        title: "Order Date",
        dataIndex: "createdAt",
        key: "createdAt",
        align: "center",
        render: (text, record) => (
          <div style={{ textAlign: "center" }}>
            <Typography>
              {moment(record?.createdAt).format("MMMM Do YYYY")}
            </Typography>
            <Typography>
              {moment(record?.createdAt).format("h:mm:ss a")}
            </Typography>
          </div>
        ),
      },
      {
        title: "Order Status",
        dataIndex: "status",
        key: "status",
        render: (text, record) => (
          <Typography>
            {record?.refundState
              ? `${
                  record?.refundState === "completed"
                    ? record?.status
                    : `Refund ${record?.refundState}`
                }`
              : record?.status}
          </Typography>
        ),
      },
      {
        title: "Actions",
        dataIndex: "actions",
        key: "actions",
        render: (text, record) => (
          <Space size="small" direction="vertical">
            {record?.invoice && (
              <Button
                type="primary"
                href={record?.invoice}
                target="_blank"
                block
              >
                Invoice
              </Button>
            )}
          </Space>
        ),
      },
    ];
    return (
      <>
        <div>
          <Table
            key={tableKey}
            columns={columns}
            dataSource={refunds}
            loading={refundsLoading}
            scroll={{ x: "max-content" }}
            pagination={{
              current: page,
              pageSize: 100,
              onChange: setPage,
              total,
              showSizeChanger: false,
              showTotal: (t) => `Total ${t} orders`,
            }}
          />
        </div>
      </>
    );
  };

  useEffect(() => {
    getRefundsData();
  }, [page, debouncedSearch, startDate, endDate]);

  return (
    <>
      <Row
        gutter={[16, 16]}
        style={{
          marginBottom: "16px",
          width: "100%",
        }}
      >
        <Col span={12}>
          <Card title="Total Refund Value" bordered={false}>
            <Typography>₹ {totalRefundValue?.toFixed(2)}</Typography>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Total Orders" bordered={false}>
            <Typography>{total}</Typography>
          </Card>
        </Col>
      </Row>
      <Row
        style={{
          marginBottom: "8px",
        }}
      >
        <Col
          span={12}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-evenly",
            flexDirection: "column",
          }}
        >
          <div style={{ display: "flex", width: "100%" }}>
            <Input
              value={search}
              minLength={2}
              className="searchbar"
              style={{ width: "100%" }}
              placeholder="Search"
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </Col>
        <Col span={12}>
          <Space
            style={{
              width: `100%`,
              justifyContent: "flex-end",
            }}
          >
            <DatePicker.RangePicker
              onChange={(date) => {
                if (date) {
                  setStartDate(date[0].startOf("day"));
                  setEndDate(date[1].endOf("day"));
                } else {
                  setStartDate(moment().add(-7, "days").startOf("day"));
                  setEndDate(moment().endOf("day"));
                }
              }}
              value={[startDate, endDate]}
            />
            <Button type="primary" onClick={getRefundsCSV}>
              Download CSV
            </Button>
          </Space>
        </Col>
      </Row>
      {refundsTable()}
    </>
  );
};

export default Refunds;
